// SPDX-License-Identifier: AGPL-3.0-or-later

import Accessibility from '@app/features/accessibility/state/Accessibility';
import {useShouldAnimate} from '@app/features/app/hooks/useShouldAnimate';
import styles from '@app/features/cosmetics/components/AvatarUltraReveal.module.css';
import {FRONT_SCALE} from '@app/features/cosmetics/constants/AvatarDecorationGeometry';
import UltraLayouts from '@app/features/cosmetics/state/UltraLayouts';
import {getCosmeticAssetURL, ULTRA_FRONT_MEDIA_MAX_SIZE} from '@app/features/cosmetics/utils/CosmeticAssetUtils';
import {loadProfilePart} from '@app/features/cosmetics/utils/UltraFrontAssets';
import * as ImageCacheUtils from '@app/features/messaging/utils/ImageCacheUtils';
import type {UltraReveal} from '@fluxer/schema/src/domains/cosmetics/CosmeticResponseSchemas';
import {observer} from 'mobx-react-lite';
import {useCallback, useEffect, useState} from 'react';

export interface AvatarUltraRevealProps {
	itemId: string;
	reveal: UltraReveal;
	size: number;
	animate: boolean;
	surface: 'compact' | 'voice' | 'profile';
}

// Micup (crystal dragon): drawn whole in front of the avatar. Chat, member and voice rows show only the still closed
// frame (one plain image: no listener, timer or portal per row); an open profile plays the intro once and then the
// loop.
export function AvatarUltraReveal({itemId, reveal, size, animate, surface}: AvatarUltraRevealProps) {
	const stillOf = (hash: string) =>
		getCosmeticAssetURL(
			'avatar_ultra',
			{item_id: itemId, asset: hash},
			size * FRONT_SCALE,
			false,
			ULTRA_FRONT_MEDIA_MAX_SIZE,
		);
	const closed = stillOf(reveal.closed);
	if (!closed) return null;
	const closedFailed = () => UltraLayouts.frontFailed(itemId);
	return (
		<span className={styles.box} aria-hidden data-flx="cosmetics.avatar-ultra-reveal.box">
			{surface === 'profile' ? (
				<RevealPlayer
					itemId={itemId}
					reveal={reveal}
					closed={closed}
					idleStill={stillOf(reveal.idle)}
					animate={animate}
					onClosedError={closedFailed}
					data-flx="cosmetics.avatar-ultra-reveal.reveal-player"
				/>
			) : (
				<Still src={closed} onError={closedFailed} data-flx="cosmetics.avatar-ultra-reveal.still" />
			)}
		</span>
	);
}

interface RevealPlayerProps {
	itemId: string;
	reveal: UltraReveal;
	closed: string;
	idleStill: string;
	animate: boolean;
	onClosedError: () => void;
}

// Reduced motion, save data and game mode (Micup out of focus) show a still: the closed frame before the intro, the
// last pose after it. An intro that has been on screen never plays again while the profile stays open.
const RevealPlayer = observer(function RevealPlayer({
	itemId,
	reveal,
	closed,
	idleStill,
	animate,
	onClosedError,
}: RevealPlayerProps) {
	const play = useShouldAnimate({kind: 'avatar', isHovering: animate && !Accessibility.useReducedMotion});
	const [played, setPlayed] = useState(false);
	const markPlayed = useCallback(() => setPlayed(true), []);
	const still = played ? idleStill : closed;
	const onStillError = played ? undefined : onClosedError;
	if (!play) return <Still src={still} onError={onStillError} data-flx="cosmetics.avatar-ultra-reveal.still" />;
	return (
		<RevealLayers
			itemId={itemId}
			reveal={reveal}
			still={still}
			idleStill={idleStill}
			onStillError={onStillError}
			played={played}
			onPlayed={markPlayed}
			data-flx="cosmetics.avatar-ultra-reveal.reveal-layers"
		/>
	);
});

interface RevealLayersProps {
	itemId: string;
	reveal: UltraReveal;
	still: string;
	idleStill: string;
	onStillError?: () => void;
	played: boolean;
	onPlayed: () => void;
}

interface LayerUrls {
	intro: string;
	idle: string;
}

// The still stays until the first moving part has loaded; the intro holds its last frame (it plays once) until the
// loop over it has loaded, so no step shows an empty box. Both parts are asked whole (600) and played from new blob
// URLs, so each open starts the intro from its first frame without the network. A failed part keeps the still.
function RevealLayers({itemId, reveal, still, idleStill, onStillError, played, onPlayed}: RevealLayersProps) {
	const urlOf = (hash: string) =>
		getCosmeticAssetURL(
			'avatar_ultra',
			{item_id: itemId, asset: hash},
			ULTRA_FRONT_MEDIA_MAX_SIZE,
			true,
			ULTRA_FRONT_MEDIA_MAX_SIZE,
		);
	const introSource = urlOf(reveal.intro);
	const idleSource = urlOf(reveal.idle);
	const [urls, setUrls] = useState<LayerUrls | null>(null);
	const [failed, setFailed] = useState(false);
	const [introShown, setIntroShown] = useState(false);
	const [idleShown, setIdleShown] = useState(false);

	useEffect(() => {
		let live = true;
		let made: LayerUrls | null = null;
		Promise.all([loadProfilePart(introSource), loadProfilePart(idleSource)]).then(
			([intro, idle]) => {
				if (!live) return;
				made = {intro: URL.createObjectURL(intro), idle: URL.createObjectURL(idle)};
				setUrls(made);
			},
			() => {
				if (live) setFailed(true);
			},
		);
		return () => {
			live = false;
			if (!made) return;
			URL.revokeObjectURL(made.intro);
			URL.revokeObjectURL(made.idle);
		};
	}, [introSource, idleSource]);

	// One timer per open profile, from the intro's first frame on screen to the loop.
	useEffect(() => {
		if (!introShown || played) return;
		const timer = setTimeout(onPlayed, reveal.intro_ms);
		return () => clearTimeout(timer);
	}, [introShown, played, onPlayed, reveal.intro_ms]);

	// An intro cut short by a pause counts as played.
	useEffect(() => (introShown ? onPlayed : undefined), [introShown, onPlayed]);

	// A pause after this point shows the last pose: fetched once while the intro plays, so it never draws empty.
	useEffect(() => {
		if (introShown) ImageCacheUtils.warmImage(idleStill);
	}, [introShown, idleStill]);

	if (failed) return <Still src={still} onError={onStillError} data-flx="cosmetics.avatar-ultra-reveal.still" />;
	const fail = () => setFailed(true);
	return (
		<>
			{introShown || idleShown ? null : (
				<Still src={still} onError={onStillError} data-flx="cosmetics.avatar-ultra-reveal.still" />
			)}
			{urls && !idleShown && (!played || introShown) ? (
				<img
					className={styles.image}
					src={urls.intro}
					alt=""
					draggable={false}
					onLoad={() => setIntroShown(true)}
					onError={fail}
					data-flx="cosmetics.avatar-ultra-reveal.intro"
				/>
			) : null}
			{urls && played ? (
				<img
					className={styles.image}
					src={urls.idle}
					alt=""
					draggable={false}
					onLoad={() => setIdleShown(true)}
					onError={fail}
					data-flx="cosmetics.avatar-ultra-reveal.idle"
				/>
			) : null}
		</>
	);
}

function Still({src, onError}: {src: string; onError?: () => void}) {
	return (
		<img
			className={styles.image}
			src={src}
			alt=""
			draggable={false}
			onError={onError}
			data-flx="cosmetics.avatar-ultra-reveal.image"
		/>
	);
}
